// @owners { team: patients-team }
import { type FertilityCycleMedicationType } from '@alto/scriptdash/alto/fertility_cycles/types/v1/fertility_cycle_medication_type';
import { useMemo } from 'react';
import { usePrescriptionsInCart } from '~shared/features/cart/hooks/usePrescriptionsInCart';
import { PRESCRIPTION_PRICING_LABELS } from '~shared/features/pricing/constants';
import { type EligiblePrescription } from '~shared/reducers/ui/progynyWizard';
import { getEligiblePrescriptionIDs } from '~shared/selectors/progyny-wizard/getEligiblePrescriptionIDs';
import { getProgynyWizardEligiblePrescriptions } from '~shared/selectors/progyny-wizard/getProgynyWizardEligiblePrescriptions';
import { useSelectorShared } from '~shared/store';
import { type Prescription } from '~shared/types';

export const useProgynyWizard = () => {
  const { prescriptions: cartPrescriptions, cartPricingTotals, prescriptionsHash } = usePrescriptionsInCart();
  const eligiblePrescriptions = useSelectorShared(getProgynyWizardEligiblePrescriptions);
  const eligiblePrescriptionIDs = useSelectorShared(getEligiblePrescriptionIDs);

  const eligiblePrescriptionsInCart = useMemo(() => {
    const eligiblePrescriptions: Prescription[] = [];
    eligiblePrescriptionIDs.forEach((id) => {
      if (!prescriptionsHash[id]) return;
      eligiblePrescriptions.push(prescriptionsHash[id]);
    });
    return eligiblePrescriptions;
  }, [prescriptionsHash, eligiblePrescriptionIDs]);

  const sigByMedicationType = eligiblePrescriptions?.reduce<Partial<Record<FertilityCycleMedicationType, string>>>(
    (acc, rxMed: EligiblePrescription) => {
      const prescription = eligiblePrescriptionsInCart.find((rx) => rx.id === rxMed.prescriptionID);

      if (prescription?.sig) {
        acc[rxMed.medicationType] = prescription.sig;
      }
      return acc;
    },
    {},
  );

  const hasItemsBilledToProgyny = cartPrescriptions.some(
    (prescription) => cartPricingTotals[prescription.id]?.reason === PRESCRIPTION_PRICING_LABELS.PROGYNY,
  );
  const shouldShowPriceSummary = cartPrescriptions.some(
    (prescription) => cartPricingTotals[prescription.id]?.reason !== PRESCRIPTION_PRICING_LABELS.PROGYNY,
  );

  return { eligiblePrescriptionsInCart, sigByMedicationType, hasItemsBilledToProgyny, shouldShowPriceSummary };
};
